import { useState, useEffect } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { updateProfile, changePassword as changePasswordService, setupAccountTotp, confirmAccountTotp, disableAccountTotp } from "../services/userService.js";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const isAdmin = true;

  const [activeTab, setActiveTab] = useState("profile");

  // ── Informations profil (contrôlé) ──────────────────────────
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName:  "",
    email:     "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved,  setProfileSaved]  = useState(false);
  const [profileError,  setProfileError]  = useState("");

  // ── Changement de mot de passe (contrôlé) ───────────────────
  const [pwdForm, setPwdForm] = useState({
    oldPassword:     "",
    newPassword:     "",
    confirmPassword: "",
  });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSaved,  setPwdSaved]  = useState(false);
  const [pwdError,  setPwdError]  = useState("");

  // ── Notifications ────────────────────────────────────────────
  const [notifPrefs, setNotifPrefs] = useState({ email: true, inapp: true, digest: "immediate" });
  const [notifSaved, setNotifSaved] = useState(false);

  // ── 2FA du compte — TOTP via app tierce (Password Boss, Authy…) ──
  const [mfaEnabled,  setMfaEnabled]  = useState(false);
  const [mfaStep,     setMfaStep]     = useState("idle"); // idle | setup | confirming
  const [mfaSecret,   setMfaSecret]   = useState("");
  const [mfaCode,     setMfaCode]     = useState("");
  const [mfaSaving,   setMfaSaving]   = useState(false);
  const [mfaError,    setMfaError]    = useState("");

  // ── Initialise les formulaires depuis le user du contexte ───
  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.firstName ?? "",
        lastName:  user.lastName  ?? "",
        email:     user.email     ?? "",
      });
      setMfaEnabled(user.mfaTotpEnabled ?? false);
    }
  }, [user]);

  // ─────────────────────────────────────────────────────────────
  //  Sauvegarde du profil  →  PUT /api/users/profile
  // ─────────────────────────────────────────────────────────────
  const saveProfile = async () => {
    setProfileError("");
    setProfileSaving(true);
    try {
      const res = await updateProfile({
        firstName: profileForm.firstName,
        lastName:  profileForm.lastName,
      });
      if (setUser && res?.data) setUser(res.data);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch (err) {
      setProfileError(err.message || "Erreur lors de la mise à jour du profil.");
    } finally {
      setProfileSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  Changement de mot de passe  →  PUT /api/users/change-password
  // ─────────────────────────────────────────────────────────────
  const changePassword = async () => {
    setPwdError("");

    if (!pwdForm.oldPassword || !pwdForm.newPassword || !pwdForm.confirmPassword) {
      setPwdError("Tous les champs sont obligatoires.");
      return;
    }
    if (pwdForm.newPassword !== pwdForm.confirmPassword) {
      setPwdError("Les mots de passe ne correspondent pas.");
      return;
    }
    if (pwdForm.newPassword.length < 8) {
      setPwdError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    setPwdSaving(true);
    try {
      await changePasswordService(pwdForm.oldPassword, pwdForm.newPassword);
      setPwdSaved(true);
      setPwdForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => setPwdSaved(false), 2000);
    } catch (err) {
      setPwdError(err.message || "Ancien mot de passe incorrect.");
    } finally {
      setPwdSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  Activation / désactivation du 2FA (email) à la connexion
  //  → PUT /api/users/mfa-toggle  { enabled: boolean }
  //
  //  Activé  : la connexion exige le code de vérification par email
  //            (comportement actuellement forcé pour tout le monde).
  //  Désactivé : connexion directe après email + mot de passe.
  // ─────────────────────────────────────────────────────────────
  // ── Étape 1 : démarrer la configuration — génère un secret ──
  const startMfaSetup = async () => {
    setMfaError("");
    setMfaSaving(true);
    try {
      const data = await setupAccountTotp();
      setMfaSecret(data.secret);
      setMfaStep("confirming");
    } catch (err) {
      setMfaError(err.message || "Impossible de générer le secret.");
    } finally {
      setMfaSaving(false);
    }
  };

  // ── Étape 2 : confirmer le premier code généré par l'app tierce ──
  const confirmMfaSetup = async () => {
    if (!mfaCode.trim()) {
      setMfaError("Saisissez le code affiché dans votre application.");
      return;
    }
    setMfaError("");
    setMfaSaving(true);
    try {
      await confirmAccountTotp(mfaCode.trim());
      setMfaEnabled(true);
      setMfaStep("idle");
      setMfaSecret("");
      setMfaCode("");
      if (setUser) setUser(prev => prev ? { ...prev, mfaTotpEnabled: true } : prev);
    } catch (err) {
      setMfaError(err.message || "Code incorrect. Vérifiez l'heure de votre appareil.");
    } finally {
      setMfaSaving(false);
    }
  };

  const cancelMfaSetup = () => {
    setMfaStep("idle");
    setMfaSecret("");
    setMfaCode("");
    setMfaError("");
  };

  // ── Désactivation ──
  const handleDisableMfa = async () => {
    setMfaError("");
    setMfaSaving(true);
    try {
      await disableAccountTotp();
      setMfaEnabled(false);
      if (setUser) setUser(prev => prev ? { ...prev, mfaTotpEnabled: false } : prev);
    } catch (err) {
      setMfaError(err.message || "Impossible de désactiver le 2FA.");
    } finally {
      setMfaSaving(false);
    }
  };

  const saveNotifPrefs = () => {
    // TODO: brancher sur un endpoint dédié quand disponible côté backend
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2000);
  };

  return (
    <>
      <Topbar title="Mon" subtitle="profil" admin={isAdmin} />
      <div className="page-body" style={{ maxWidth: 720 }}>

        {/* Avatar header */}
        <div className="itm-card anim-fade-up" style={{ marginBottom: 20, padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ width: 72, height: 72, borderRadius: 18, background: "linear-gradient(135deg,var(--accent3),var(--accent))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 800, color: "#fff", fontFamily: "Syne,sans-serif", flexShrink: 0 }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div>
              <div style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 800, color: "var(--text-1)", marginBottom: 4 }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 6 }}>{user?.email}</div>
              <span className={`badge-status ${isAdmin ? "bs-active" : "bs-open"}`}>{isAdmin ? "Administrateur" : "Utilisateur client"}</span>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="itm-tabs">
          {[{ id: "profile", label: "Informations" }, { id: "security", label: "Sécurité" }, { id: "notifications", label: "Notifications" }].map(t => (
            <div key={t.id} className={`itm-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>{t.label}</div>
          ))}
        </div>

        {/* Tab Profil */}
        {activeTab === "profile" && (
          <div className="itm-card anim-fade-in">

            {profileError && (
              <div style={{ background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>
                <i className="bi bi-exclamation-triangle-fill me-2" />{profileError}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="itm-label">Prénom</label>
                <input
                  className="itm-input"
                  value={profileForm.firstName}
                  onChange={e => setProfileForm(p => ({ ...p, firstName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="itm-label">Nom</label>
                <input
                  className="itm-input"
                  value={profileForm.lastName}
                  onChange={e => setProfileForm(p => ({ ...p, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="itm-label">Email</label>
                {/* Email en lecture seule — modification via un flux dédié si besoin */}
                <input className="itm-input" type="email" value={profileForm.email} readOnly style={{ opacity: .6, cursor: "not-allowed" }} />
              </div>
              <div className="form-group">
                <label className="itm-label">Téléphone</label>
                <input className="itm-input" placeholder="+33 6 00 00 00 00" />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
              <button className="btn-itm btn-itm-primary" onClick={saveProfile} disabled={profileSaving}>
                {profileSaving
                  ? <><i className="bi bi-arrow-repeat spin" /> Enregistrement…</>
                  : profileSaved
                    ? <><i className="bi bi-check2" /> Enregistré !</>
                    : <><i className="bi bi-floppy" /> Enregistrer</>
                }
              </button>
            </div>
          </div>
        )}

        {/* Tab Sécurité — mot de passe */}
        {activeTab === "security" && (
          <div className="itm-card anim-fade-in">
            <div className="card-title" style={{ marginBottom: 16 }}>Changer le mot de passe</div>

            {pwdError && (
              <div style={{ background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>
                <i className="bi bi-exclamation-triangle-fill me-2" />{pwdError}
              </div>
            )}

            <div className="form-group">
              <label className="itm-label">Mot de passe actuel</label>
              <input
                className="itm-input" type="password" placeholder="••••••••"
                value={pwdForm.oldPassword}
                onChange={e => setPwdForm(p => ({ ...p, oldPassword: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="itm-label">Nouveau mot de passe</label>
              <input
                className="itm-input" type="password" placeholder="Minimum 8 caractères"
                value={pwdForm.newPassword}
                onChange={e => setPwdForm(p => ({ ...p, newPassword: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="itm-label">Confirmer le nouveau mot de passe</label>
              <input
                className="itm-input" type="password" placeholder="••••••••"
                value={pwdForm.confirmPassword}
                onChange={e => setPwdForm(p => ({ ...p, confirmPassword: e.target.value }))}
              />
            </div>
            <div style={{ padding: "10px 14px", background: "var(--accent-dim)", borderRadius: 10, fontSize: 12, color: "var(--accent)", marginBottom: 16 }}>
              <i className="bi bi-shield-check me-2" />
              Règles : min. 8 caractères, 1 majuscule, 1 chiffre, 1 caractère spécial.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn-itm btn-itm-primary" onClick={changePassword} disabled={pwdSaving}>
                {pwdSaving
                  ? <><i className="bi bi-arrow-repeat spin" /> Modification…</>
                  : pwdSaved
                    ? <><i className="bi bi-check2" /> Modifié !</>
                    : <><i className="bi bi-lock" /> Changer le mot de passe</>
                }
              </button>
            </div>
          </div>
        )}

        {/* Tab Sécurité — 2FA du compte via application tierce */}
        {activeTab === "security" && (
          <div className="itm-card anim-fade-in" style={{ marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--accent-dim)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-shield-lock-fill" style={{ color: "var(--accent)", fontSize: 16 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="card-title" style={{ marginBottom: 0 }}>Double authentification</div>
                <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                  Utilisez une application d'authentification (Password Boss, Authy, Google Authenticator…) au lieu du code par email.
                </div>
              </div>
            </div>

            {mfaError && (
              <div style={{ background: "rgba(220,38,38,.08)", border: "1px solid rgba(220,38,38,.2)", borderRadius: 10, padding: "10px 14px", margin: "12px 0", fontSize: 13, color: "#DC2626" }}>
                <i className="bi bi-exclamation-triangle-fill me-2" />{mfaError}
              </div>
            )}

            {/* ── État : activé ── */}
            {mfaEnabled && mfaStep === "idle" && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "rgba(34,197,94,.08)", border: "1px solid rgba(34,197,94,.2)", borderRadius: 10, marginBottom: 12 }}>
                  <i className="bi bi-check-circle-fill" style={{ color: "#22C55E" }} />
                  <span style={{ fontSize: 13, color: "#16A34A", fontWeight: 500 }}>
                    2FA par application activé — le code email n'est plus utilisé.
                  </span>
                </div>
                <button className="btn-itm btn-itm-outline" onClick={handleDisableMfa} disabled={mfaSaving}
                  style={{ color: "var(--danger)", borderColor: "rgba(239,68,68,.3)" }}>
                  {mfaSaving ? <><i className="bi bi-arrow-repeat spin" /> Désactivation…</> : <><i className="bi bi-x-circle" /> Désactiver le 2FA</>}
                </button>
              </div>
            )}

            {/* ── État : désactivé, pas encore en cours de config ── */}
            {!mfaEnabled && mfaStep === "idle" && (
              <button
                onClick={startMfaSetup}
                disabled={mfaSaving}
                style={{ width: "100%", marginTop: 12, background: "var(--navy-soft)", border: "none", cursor: mfaSaving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderRadius: 10 }}
              >
                <i className="bi bi-shield-plus" style={{ fontSize: 16, color: "var(--accent)" }} />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontWeight: 600, color: "var(--text-1)", fontSize: 13 }}>
                    {mfaSaving ? "Génération…" : "Activer le 2FA"}
                  </div>
                  <div style={{ fontSize: 11, marginTop: 2, color: "var(--text-3)" }}>Générer un secret pour votre application</div>
                </div>
              </button>
            )}

            {/* ── État : secret généré, en attente de confirmation ── */}
            {mfaStep === "confirming" && (
              <div style={{ marginTop: 12, background: "var(--navy-soft)", borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-1)", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="bi bi-shield-lock-fill" style={{ color: "var(--accent)" }} />
                  Configurer le 2FA
                </div>

                <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 12, lineHeight: 1.7 }}>
                  <strong style={{ color: "var(--text-1)" }}>Comment obtenir le secret ?</strong>
                  <ol style={{ margin: "6px 0 0 16px", padding: 0 }}>
                    <li>Ajoutez une entrée dans votre gestionnaire (Password Boss, Authy…)</li>
                    <li>Choisissez "Saisir la clé manuellement"</li>
                    <li>Collez le secret ci-dessous dans l'application</li>
                    <li>Revenez ici saisir le code à 6 chiffres généré</li>
                  </ol>
                </div>

                {/* Secret affiché pour copie */}
                <div style={{
                  fontFamily: "monospace", fontSize: 15, fontWeight: 700, letterSpacing: ".08em",
                  background: "#fff", border: "1.5px solid var(--border)", borderRadius: 8,
                  padding: "10px 12px", marginBottom: 14, textAlign: "center", color: "var(--text-1)",
                  userSelect: "all",
                }}>
                  {mfaSecret}
                </div>

                {/* Saisie du code de confirmation */}
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="itm-label">Code à 6 chiffres</label>
                  <input
                    className="itm-input"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    style={{ fontFamily: "monospace", fontSize: 16, letterSpacing: ".1em", textAlign: "center" }}
                  />
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-itm btn-itm-outline" onClick={cancelMfaSetup} disabled={mfaSaving} style={{ flex: 1 }}>
                    Annuler
                  </button>
                  <button className="btn-itm btn-itm-primary" onClick={confirmMfaSetup} disabled={mfaSaving || mfaCode.length < 6} style={{ flex: 1 }}>
                    {mfaSaving ? <><i className="bi bi-arrow-repeat spin" /> Vérification…</> : <><i className="bi bi-check-lg" /> Activer</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Notifications */}
        {activeTab === "notifications" && (
          <div className="itm-card anim-fade-in">
            <div className="card-title" style={{ marginBottom: 16 }}>Préférences de notification</div>
            {[
              { key: "email", label: "Notifications par email", desc: "Recevoir des emails lors des changements de statut" },
              { key: "inapp", label: "Notifications in-app",    desc: "Afficher les notifications dans l'interface" },
            ].map(p => (
              <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)" }}>{p.label}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>{p.desc}</div>
                </div>
                <div style={{
                  width: 44, height: 24, borderRadius: 12, cursor: "pointer", transition: "background .2s",
                  background: notifPrefs[p.key] ? "var(--accent)" : "var(--navy-soft)",
                  border: "1px solid var(--border)", position: "relative"
                }} onClick={() => setNotifPrefs(prev => ({ ...prev, [p.key]: !prev[p.key] }))}>
                  <div style={{
                    width: 18, height: 18, borderRadius: "50%", background: "#fff",
                    position: "absolute", top: 2, transition: "left .2s",
                    left: notifPrefs[p.key] ? 22 : 2
                  }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              <label className="itm-label">Fréquence des emails</label>
              <select className="itm-select" style={{ width: "100%" }} value={notifPrefs.digest} onChange={e => setNotifPrefs(p => ({ ...p, digest: e.target.value }))}>
                <option value="immediate">Immédiat (à chaque événement)</option>
                <option value="daily">Résumé quotidien</option>
                <option value="weekly">Résumé hebdomadaire</option>
              </select>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button className="btn-itm btn-itm-primary" onClick={saveNotifPrefs}>
                {notifSaved ? <><i className="bi bi-check2" /> Enregistré !</> : <><i className="bi bi-floppy" /> Enregistrer</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}